import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
// @mui
import {
  Card,
  Stack,
  Container,
  Typography,
  CircularProgress,
  Box,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
} from "@mui/material";
import { Save, Add, Delete } from "@mui/icons-material";
import { get, put } from "src/utils/networkUtils";

// ----------------------------------------------------------------------

export default function UserLevelsEditPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editAttributes, setEditAttributes] = useState(null);
  const [editableLevels, setEditableLevels] = useState([]);

  useEffect(() => {
    setLoading(true);

    Promise.all([get(`/admin/user-level/attributes`), get(`/admin/user-level`)])
      .then(([attributesResponse, levelsResponse]) => {
        setEditAttributes(attributesResponse.attributes);
        setEditableLevels(levelsResponse.levels || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading data:", error);
        setLoading(false);
      });
  }, []);

  const handleLevelChange = (index, field, value) => {
    const updatedLevels = [...editableLevels];
    updatedLevels[index] = {
      ...updatedLevels[index],
      [field]:
        field === "startRange" || field === "endRange"
          ? parseFloat(value) / 100
          : value,
    };
    setEditableLevels(updatedLevels);
  };

  const handleAttributeChange = (index, field, value) => {
    const updatedAttributes = [...editAttributes];
    updatedAttributes[index] = {
      ...updatedAttributes[index],
      [field]: field === "weight" ? parseFloat(value) / 100 : value,
    };
    setEditAttributes(updatedAttributes);
  };

  const handleAddLevel = () => {
    const newLevel = {
      name: "",
      color: "#ffffff",
      startRange: 0,
      endRange: 0,
    };
    setEditableLevels([...editableLevels, newLevel]);
  };

  const handleDeleteLevel = (index) => {
    const updatedLevels = editableLevels.filter((_, i) => i !== index);
    setEditableLevels(updatedLevels);
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);

      // Prepare levels data for API
      const levelsToSave = editableLevels.map((level) => ({
        userValueLevelId: level.userValueLevelId || undefined,
        name: level.name,
        color: level.color,
        startRange: level.startRange,
        endRange: level.endRange,
      }));

      // Prepare attributes data for API
      const attributesToSave = editAttributes.map((attribute) => ({
        userValueAttributeId: attribute.userValueAttributeId,
        name: attribute.name,
        weight: attribute.weight,
      }));

      // Save both levels and attributes in parallel
      const [levelsResponse, attributesResponse] = await Promise.all([
        put("/admin/user-level", {
          levels: levelsToSave,
        }),
        put("/admin/user-level/attributes", {
          attributes: attributesToSave,
        }),
      ]);

      console.log("Levels saved successfully:", levelsResponse);
      console.log("Attributes saved successfully:", attributesResponse);

      // Optionally show success message or navigate back
      // navigate('/dashboard/user-levels');
    } catch (error) {
      console.error("Error saving data:", error);
      // Handle error - you might want to show a toast/snackbar here
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>User Levels Edit</title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            User Levels Edit
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : <Save />
            }
            onClick={handleSaveAll}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Stack>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Card sx={{ mb: 3 }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }} gutterBottom>
                  User Levels
                </Typography>
                {loading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: 200,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {editableLevels &&
                      editableLevels.length > 0 &&
                      editableLevels.map((level, index) => (
                        <Grid container spacing={3} key={index} sx={{ mb: 2 }}>
                          <Grid item xs={2}>
                            <TextField
                              label="Name"
                              value={level.name || ""}
                              onChange={(e) =>
                                handleLevelChange(index, "name", e.target.value)
                              }
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={3} sx={{ position: "relative" }}>
                            <TextField
                              label="Color"
                              value={level.color || ""}
                              onChange={(e) =>
                                handleLevelChange(
                                  index,
                                  "color",
                                  e.target.value
                                )
                              }
                              size="small"
                              sx={{ minWidth: 120 }}
                              InputProps={{
                                startAdornment: (
                                  <Box
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      backgroundColor: level.color || "#ffffff",
                                      borderRadius: 1,
                                      border: "1px solid",
                                      borderColor: "divider",
                                      mr: 1,
                                      cursor: "pointer",
                                    }}
                                  />
                                ),
                              }}
                            />

                            <input
                              type="color"
                              value={level.color || "#ffffff"}
                              onChange={(e) =>
                                handleLevelChange(
                                  index,
                                  "color",
                                  e.target.value
                                )
                              }
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                opacity: 0,
                                cursor: "pointer",
                                border: "none",
                                padding: 0,
                                margin: 0,
                              }}
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Start Range</InputLabel>
                              <Select
                                value={Math.round(
                                  (level.startRange || 0) * 100
                                )}
                                onChange={(e) =>
                                  handleLevelChange(
                                    index,
                                    "startRange",
                                    e.target.value
                                  )
                                }
                                label="Start Range"
                              >
                                {Array.from({ length: 101 }, (_, i) => i).map(
                                  (num) => (
                                    <MenuItem key={num} value={num}>
                                      {num}%
                                    </MenuItem>
                                  )
                                )}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>End Range</InputLabel>
                              <Select
                                value={Math.round((level.endRange || 0) * 100)}
                                onChange={(e) =>
                                  handleLevelChange(
                                    index,
                                    "endRange",
                                    e.target.value
                                  )
                                }
                                label="End Range"
                              >
                                {Array.from({ length: 101 }, (_, i) => i).map(
                                  (num) => (
                                    <MenuItem key={num} value={num}>
                                      {num}%
                                    </MenuItem>
                                  )
                                )}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid
                            item
                            xs={1}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <IconButton
                              onClick={() => handleDeleteLevel(index)}
                              color="error"
                              size="small"
                              sx={{
                                "&:hover": {
                                  backgroundColor: "error.light",
                                  color: "white",
                                },
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Grid>
                        </Grid>
                      ))}

                    {/* Add Level Button */}
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={handleAddLevel}
                        fullWidth
                        sx={{ py: 1.5 }}
                      >
                        Add Level
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Card sx={{ mb: 3 }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }} gutterBottom>
                  Player Value Attributes
                </Typography>
                {loading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: 200,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {editAttributes &&
                      editAttributes.length > 0 &&
                      editAttributes.map((attribute, index) => (
                        <Grid container spacing={3} key={index} sx={{ mb: 2 }}>
                          <Grid item xs={8}>
                            <TextField
                              label="Attribute Name"
                              value={attribute.name || ""}
                              onChange={(e) =>
                                handleAttributeChange(
                                  index,
                                  "name",
                                  e.target.value
                                )
                              }
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Weight</InputLabel>
                              <Select
                                value={Math.round(
                                  (attribute.weight || 0) * 100
                                )}
                                onChange={(e) =>
                                  handleAttributeChange(
                                    index,
                                    "weight",
                                    e.target.value
                                  )
                                }
                                label="Weight"
                              >
                                {Array.from({ length: 101 }, (_, i) => i).map(
                                  (num) => (
                                    <MenuItem key={num} value={num}>
                                      {num}%
                                    </MenuItem>
                                  )
                                )}
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      ))}

                    {/* Total Weight Summary */}
                    {editAttributes && editAttributes.length > 0 && (
                      <Grid
                        container
                        spacing={3}
                        sx={{ mb: 2, pt: 2, borderColor: "divider" }}
                      >
                        <Grid item xs={8}>
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{ pt: 1 }}
                          >
                            Total Weight:
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{
                              pt: 1,
                              color: (() => {
                                const totalWeight = editAttributes.reduce(
                                  (sum, attr) => sum + (attr.weight || 0),
                                  0
                                );
                                const totalPercentage = Math.round(
                                  totalWeight * 100
                                );
                                return totalPercentage === 100
                                  ? "success.main"
                                  : "error.main";
                              })(),
                            }}
                          >
                            {(() => {
                              const totalWeight = editAttributes.reduce(
                                (sum, attr) => sum + (attr.weight || 0),
                                0
                              );
                              return Math.round(totalWeight * 100);
                            })()}
                            %
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                  </>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
